<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'TlbsJUEMap',
  
})
</script>
<template>
  <div
    ref="mapContainerRef"
    class="tlbs-jue-map"
  />
</template>

<script setup lang="ts" name="TlbsJUEMap">
import { mapAttrsHandlers } from './map-attrs-handlers';
import useLayerManager from './useLayerManager/index';
import useTools from './tools/useTools';
import omit from 'lodash/omit';
import { WatchStopHandle } from 'vue';

const reinitProps = ['showControl', 'mapZoomType', 'subkey'];

interface Props {
  latitude?: number;
  longitude?: number;
  zoom?: number;
  rotation?: number;
  pitch?: number;
  scale?: number;
  draggable?: boolean;
  viewMode?: '2D' | '3D';
  showControl?: boolean;
  zoomRange?: number[];
  offset?: OffsetOption;
  subkey?: string;
  mapStyleId?: string;
  measureToolType?: string;
  enableRealtimeLatlng?: boolean;
  enableRoam?: boolean;
  roamDuration?: number;
  roamLoop?: number;
  roamData?: RoamKeyFrame[];
  simulatorTime?: string;
  simulatorDuration?: number;
  layers?: MapLayer[];
}

const props = withDefaults(defineProps<Props>(), {
  latitude: 39.908755,
  longitude: 116.397476,
  zoom: 8,
  rotation: 0,
  pitch: 0,
  scale: 1,
  draggable: true,
  viewMode: '3D',
  showControl: true,
  zoomRange: () => [3, 20],
  offset: () => ({ x: 0, y: 0 }),
  subkey: '',
  mapStyleId: '',
  measureToolType: '',
  enableRealtimeLatlng: false,
  enableRoam: false,
  roamDuration: 10,
  roamLoop: 1,
  roamData: () => [],
  simulatorTime: '09:30',
  simulatorDuration: 10,
  layers: () => [],
});

const emit = defineEmits<{
  (e: 'loaded' | 'closed' | 'animationEnded' | 'animationStopped'): void;
  (e: 'click', evt: MapEvent): void;
  (e: 'animationPlaying', evt: AnimationEvent): void;
}>();

const mapContainerRef = ref();

const mapInstance = shallowRef<LooseObject | null>(null);

let clearAllLayers: undefined | (() => void);
let mapToolAttrsHandlers: LooseObject;
let destroyTools: () => void;
const watcherList = shallowRef<WatchStopHandle[]>([]);

onMounted(() => {
  initMap();
});

const initMap = (): void => {
  const options = {
    center: new JUE.LatLng(props.latitude, props.longitude),
    zoom: props.zoom,
    rotation: props.rotation,
    pitch: props.pitch,
    scale: props.scale,
    draggable: props.draggable,
    viewMode: props.viewMode,
    showControl: props.showControl,
    minZoom: props.zoomRange[0],
    maxZoom: props.zoomRange[1],
    offset: props.offset,
    subkey: props.subkey,
  };
  // todo ts
  if (props.mapStyleId) options.mapStyleId = props.mapStyleId;
  mapInstance.value = new JUE.Map(mapContainerRef.value, options);
  ({ mapToolAttrsHandlers, destroyTools } = useTools(mapInstance.value, mapContainerRef.value, props));
  if (props.layers.length) {
    clearAllLayers = useLayerManager(mapInstance.value, toRef(props, 'layers'))?.clearAllLayers;
  }
  setMapEvnets(mapInstance.value);
  setPropsWatcher();
};


const setPropsWatcher = (): void => {
  Object.keys(omit(props, 'layers')).forEach((attr) => {
    watcherList.value.push(watch(() => props[attr], (newVal) => {
      if (reinitProps.includes(attr)) {
        destroyMap();
        initMap();
      } else {
        if (mapAttrsHandlers[attr]) {
          mapAttrsHandlers[attr](newVal, mapInstance.value);
        } else if (mapToolAttrsHandlers[attr]) {
          mapToolAttrsHandlers[attr](newVal);
        } else {
          console.warn('无法处理该属性变化：', attr);
        }
      }
    }));
  });
  // 针对图层做特殊监听
  watcherList.value.push(watch(() => props.layers, () => {
    clearAllLayers?.();
    ({ clearAllLayers } = useLayerManager(mapInstance.value, toRef(props, 'layers')));
  }));
};

const setMapEvnets = (mapInstance: LooseObject | null): void => {
  mapInstance
    ?.on('loaded', () => emit('loaded'))
    .on('closed', () => emit('closed'))
    .on('click', (evt: MapEvent) => emit('click', evt))
    .on('animation_playing', (evt: AnimationEvent) => emit('animationPlaying', evt))
    .on('animation_ended', () => emit('animationEnded'))
    .on('animation_stopped', () => emit('animationStopped'));
};

onUnmounted(() => {
  destroyMap();
});

const destroyMap = (): void => {
  destroyTools();
  clearAllLayers?.();
  mapInstance.value?.destroy();
  mapInstance.value = null;
  watcherList.value.forEach(unwatch => unwatch());
  watcherList.value = [];
};

defineExpose({
  map: mapInstance,
});
</script>

